/**
 * ViewAsClientSwitcher — picker in the TopBar.
 * Admin: all clients. Team/AM: clients they're assigned to. Client role: hidden.
 * Setting a client stores the id in localStorage and refreshes the app so every
 * request thereafter sends `X-Impersonate-Client-Id`.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Eye, EyeOff, Check, ChevronDown } from "lucide-react";

const KEY = "tt_impersonate_client_id";

function startImpersonation(clientId) {
  if (!clientId) {
    localStorage.removeItem(KEY);
  } else {
    localStorage.setItem(KEY, clientId);
  }
  // Reload so the whole app rehydrates with the new identity (ws, auth/me, etc).
  window.location.reload();
}

export default function ViewAsClientSwitcher() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [impersonating, setImpersonating] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  // Which "real" role is this (unaffected by impersonation we might have on)?
  const realRole = user?._impersonator_role || user?.role;
  const eligible = realRole === "admin" || realRole === "team" || realRole === "account_manager";

  useEffect(() => {
    if (!eligible) return;
    api.get("/auth/impersonatable-clients").then((r) => {
      setList(r.data?.clients || []);
      setImpersonating(!!r.data?.impersonating);
      setCurrentId(r.data?.current_client_id || null);
    }).catch(() => {});
  }, [eligible]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!eligible) return null;

  const filtered = q.trim()
    ? list.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()))
    : list;

  const activeName = impersonating ? list.find((c) => c.id === currentId)?.name : null;

  return (
    <div className="relative" ref={rootRef}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid="view-as-client-trigger"
        className={`inline-flex items-center gap-1.5 h-7 px-2 rounded border text-[11px] transition-colors ${
          impersonating
            ? "bg-amber-900/40 text-amber-200 border-amber-800 hover:bg-amber-900/60"
            : "bg-transparent text-zinc-400 border-zinc-800 hover:border-zinc-600 hover:text-zinc-200"
        }`}>
        {impersonating ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
        <span className="hidden md:inline">
          {impersonating ? `As ${activeName || "client"}` : "View as client"}
        </span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-[#0a0a0a] border border-zinc-800 rounded shadow-2xl z-50"
          data-testid="view-as-client-menu">
          <div className="p-2 border-b border-zinc-900">
            <div className="t-eyebrow text-[9px] mb-1.5">View dashboard as</div>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${list.length} clients…`}
              className="w-full bg-[#111] border border-zinc-800 rounded h-7 px-2 text-[12px] text-zinc-200 outline-none focus:border-zinc-600"
              data-testid="view-as-client-search" />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {impersonating && (
              <button type="button" onClick={() => startImpersonation(null)}
                data-testid="view-as-client-exit"
                className="w-full text-left flex items-center gap-2 px-3 py-1.5 text-[12px] text-amber-300 hover:bg-zinc-900">
                <EyeOff className="w-3 h-3" /> Exit client view
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-[11px] text-zinc-500">
                {list.length === 0 ? "No clients assigned to you." : "No match."}
              </div>
            ) : (
              filtered.map((c) => {
                const on = c.id === currentId;
                return (
                  <button key={c.id} type="button" onClick={() => startImpersonation(c.id)}
                    data-testid={`view-as-client-${c.slug || c.id}`}
                    className="w-full text-left flex items-center gap-2 px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-zinc-900">
                    {c.header_logo_url ? (
                      <img src={c.header_logo_url} alt="" className="w-4 h-4 rounded object-cover border border-zinc-800" />
                    ) : (
                      <span className="w-4 h-4 rounded bg-zinc-800" />
                    )}
                    <span className="flex-1 truncate">{c.name}</span>
                    {on && <Check className="w-3 h-3 text-emerald-400" />}
                  </button>
                );
              })
            )}
          </div>
          <div className="px-3 py-1.5 border-t border-zinc-900 text-[10px] text-zinc-600 font-mono">
            Impersonation is logged. You'll see exactly what the client sees.
          </div>
        </div>
      )}
    </div>
  );
}

/** Fixed banner shown when a staff user is viewing as a client. */
export function ImpersonationBanner() {
  const { user } = useAuth();
  if (!user?._impersonator_id) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-[80] bg-amber-500/90 text-black text-[12px] font-mono flex items-center justify-center gap-3 py-1 px-3 shadow-md"
      data-testid="impersonation-banner">
      <Eye className="w-3.5 h-3.5" />
      <span>
        Viewing as <strong>{user.client_name || "client"}</strong> · staff view hidden
      </span>
      <button onClick={() => startImpersonation(null)}
        data-testid="impersonation-banner-exit"
        className="ml-2 bg-black/80 text-white px-2 h-6 rounded hover:bg-black transition-colors">
        Exit
      </button>
    </div>
  );
}
